// -----------------------------------------------------------------------------
// preflight/gate-screen.ts — the entry gate UI.
//
// Three outcomes (CLAUDE.md §5):
//   • full     → no gate, straight into the editor.
//   • degraded → straight into the editor, but show a dismissible banner that
//                explains what will be slower/limited. (We never hard-block.)
//   • blocked  → a full-screen checklist of what is missing + a recommendation,
//                plus a "Try anyway" override so the user is never trapped.
//
// main.ts calls showBlockedGate() (which resolves when the user overrides) and
// buildCapabilityBanner() (mounted inside the editor for the degraded case).
// We never fail silently: every missing capability is named on screen.
// -----------------------------------------------------------------------------
import type { Capabilities } from './detect';
import { el, clear } from '../ui/dom';

// One checklist row: a coloured dot + label + short status.
function checkRow(ok: boolean, label: string, detail: string): HTMLElement {
  return el(
    'div',
    { class: 'zf-check ' + (ok ? 'zf-check-ok' : 'zf-check-bad') },
    el('span', { class: 'zf-check-dot' }, ok ? '✓' : '✕'),
    el('span', { class: 'zf-check-label' }, label),
    el('span', { class: 'zf-check-detail' }, detail),
  );
}

// Build the requirement checklist shown on the blocked screen.
function buildChecklist(caps: Capabilities): HTMLElement {
  const list = el('div', { class: 'zf-checklist' });
  list.append(
    checkRow(caps.webgl2, 'WebGL2 compositor', caps.webgl2 ? 'available' : 'missing — preview can’t run'),
    checkRow(caps.videoDecode, 'H.264 video decode', caps.videoDecode ? 'available' : 'missing — media can’t play'),
    checkRow(caps.webAudio, 'Web Audio', caps.webAudio ? 'available' : 'missing — audio can’t be processed'),
    checkRow(caps.videoEncodeSW, 'Video encode (export)', caps.videoEncodeHW ? 'hardware' : caps.videoEncodeSW ? 'software only' : 'FFmpeg.wasm fallback'),
    checkRow(caps.audioEncodeAAC || caps.audioEncodeOpus, 'Audio encode (export)', caps.audioEncodeAAC ? 'AAC' : caps.audioEncodeOpus ? 'Opus' : 'FFmpeg.wasm fallback'),
    checkRow(caps.opfs, 'Scratch storage', caps.opfs ? 'OPFS' : 'IndexedDB fallback'),
    checkRow(caps.crossOriginIsolated, 'Cross-origin isolation', caps.crossOriginIsolated ? 'on' : 'off (multithreaded export disabled)'),
  );
  return list;
}

// Full-screen blocked gate. Resolves only when the user clicks "Try anyway".
export function showBlockedGate(root: HTMLElement, caps: Capabilities): Promise<void> {
  return new Promise<void>((resolve) => {
    clear(root);
    root.className = '';

    const reasons = el('ul', { class: 'zf-reasons' });
    for (const r of caps.reasons) reasons.append(el('li', {}, r));

    const tryAnyway = el(
      'button',
      {
        class: 'zf-btn zf-btn-primary',
        onclick: () => resolve(),
      },
      'Try anyway',
    );

    const screen = el(
      'div',
      { class: 'zf-gate' },
      el('div', { class: 'zf-gate-card' },
        el('h1', { class: 'zf-gate-title' }, 'ZeroFrame can’t run fully here'),
        el('p', { class: 'zf-gate-sub' },
          'Your browser is missing something ZeroFrame needs. ZeroFrame is a 100% in-browser editor — your media never leaves your machine, so it depends on modern browser media APIs.'),
        reasons,
        buildChecklist(caps),
        el('p', { class: 'zf-gate-rec' },
          'Recommended: the latest Chrome or Edge, or Safari 26+. These ship the WebCodecs + WebGL2 features ZeroFrame relies on.'),
        el('div', { class: 'zf-gate-actions' }, tryAnyway),
      ),
    );
    root.append(screen);
  });
}

// Dismissible banner for the degraded tier (or after a blocked override).
// Returns null when there is nothing to warn about (full tier, no reasons).
export function buildCapabilityBanner(caps: Capabilities): HTMLElement | null {
  if (caps.tier === 'full' || caps.reasons.length === 0) return null;

  const banner = el('div', { class: 'zf-banner', role: 'status' });
  const list = el('ul', { class: 'zf-banner-list' });
  for (const r of caps.reasons) list.append(el('li', {}, r));

  const dismiss = el(
    'button',
    {
      class: 'zf-banner-close',
      title: 'Dismiss',
      'aria-label': 'Dismiss',
      onclick: () => banner.remove(),
    },
    '✕',
  );

  banner.append(
    el('div', { class: 'zf-banner-head' },
      el('strong', {}, caps.tier === 'blocked' ? 'Running in override mode' : 'Running in degraded mode'),
      dismiss,
    ),
    list,
  );
  return banner;
}
